import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { UserService } from '../../Modules/Users/Services/user.service';
import { RbacService } from '../../Modules/Rbac/Services/rbac.service';
import { ImpersonationService } from '../../Modules/Users/Services/impersonation.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { ExternalLinkService } from '../../Modules/Shared/Services/external-link.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { SystemRole } from '../../Enums/system-role.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { PermissionGrant } from '../../Models/backoffice/permission-grant.model';
import { PermissionScope } from '../../Models/backoffice/permission-scope.model';
import { TenantUser } from '../../Models/backoffice/tenant-user.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { TenantUserShowResponse } from '../../Interfaces/api/backoffice/user.interface';
import { UserScopesResponse } from '../../Interfaces/api/backoffice/rbac.interface';

@Injectable()
export class UserDetailFacade {
  private readonly _userService = inject(UserService);
  private readonly _rbacService = inject(RbacService);
  private readonly _impersonationService = inject(ImpersonationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _externalLink = inject(ExternalLinkService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _userId = signal<string>('');
  private readonly _isMutating = signal<boolean>(false);

  private readonly _user = httpResource<LaravelDefaultResponse<TenantUserShowResponse>>(() =>
    this._userId() ? this._userService.showRequest(this._userId()) : undefined,
  );

  private readonly _scopes = httpResource<LaravelDefaultResponse<UserScopesResponse>>(() =>
    this._userId() ? this._rbacService.userScopesRequest(this._userId()) : undefined,
  );

  readonly userId = computed<string>(() => this._userId());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly user = computed<TenantUser | null>(() => this._userService.toDetail(this._resourceState.valueOf(this._user)));

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._user.status()),
  );

  readonly rolePermissions = computed<PermissionGrant[]>(() =>
    this._rbacService.toRolePermissions(this._resourceState.valueOf(this._scopes)),
  );

  readonly scopes = computed<PermissionScope[]>(() =>
    this._rbacService.toUserScopes(this._resourceState.valueOf(this._scopes)),
  );

  readonly scopesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._scopes.status()),
  );

  readonly canImpersonate = computed<boolean>(
    () => this._operatorFacade.atLeast(SystemRole.BACKOFFICE_ADMIN) && !this.user()?.isOperator,
  );

  load(userId: string): void {
    this._userId.set(userId);
  }

  reload(): void {
    this._user.reload();
    this._scopes.reload();
  }

  impersonate(reason: string | null): void {
    const userId = this._userId();

    if (!userId) {
      return;
    }

    this._isMutating.set(true);

    this._impersonationService
      .issue(userId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => this._afterImpersonation(response.callback_url),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterImpersonation(callbackUrl: string): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar('backoffice.users.impersonation.issued', { type: 'success' });
    this._externalLink.openInNewTab(callbackUrl);
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
